import type { ActivityLanguageValue, ParticipationValue } from '@yoyoclub/shared';
import { Calendar, Clock, Heart, Share2, Smile, User, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useSiteActions } from '../site/siteActionsContext';

import { iconBtn } from './redesign';

interface ActivityCardProps {
  /** Slug (activities and courses) or pass id; used for the detail link. */
  id: string;
  /** Database id; needed to save the activity or course. */
  activityId?: string;
  title: string;
  host: string;
  location: string;
  date: string;
  time: string;
  /** Start time alone; cards show date + start (prototype). Falls back to `time`. */
  startTime?: string;
  price: number;
  adultPrice?: number;
  /** True when displayed price varies across sessions — card prefixes "From"/"起". */
  priceVaries?: boolean;
  childRequired?: boolean;
  ageMin?: number;
  ageMax?: number;
  ageRange: string;
  imageUrl: string;
  spotsLeft?: number;
  itemType?: 'activity' | 'pass' | 'course';
  totalSessions?: number;
  participation?: ParticipationValue;
  language?: ActivityLanguageValue;
}

const PARTICIPATION_ICON = { PARENT_CHILD: Users, KID_ONLY: Smile, ADULT_ONLY: User } as const;
const PARTICIPATION_TONE = {
  PARENT_CHILD: 'text-primary-dark',
  KID_ONLY: 'text-kid-blue',
  ADULT_ONLY: 'text-brown',
} as const;

/**
 * Activity / course / pass card (docs/design-reference/homepage-prototype.html `.ac`).
 * The title link stretches over the whole card; share and save sit above it, so they
 * only run their own action (PRD §4.1).
 */
export default function ActivityCard({
  id,
  activityId,
  title,
  location,
  date,
  time,
  startTime,
  price,
  adultPrice,
  priceVaries,
  childRequired,
  ageMin,
  ageMax,
  ageRange,
  imageUrl,
  spotsLeft,
  itemType = 'activity',
  totalSessions,
  participation,
  language,
}: ActivityCardProps) {
  const { t } = useTranslation(['common']);
  const { isFavorite, toggleFavorite, share } = useSiteActions();

  const href =
    itemType === 'pass'
      ? `/passes/${id}`
      : itemType === 'course'
        ? `/courses/${id}`
        : `/activity/${id}`;
  const canSave = itemType !== 'pass' && !!activityId;
  const saved = canSave && isFavorite('activity', activityId!);

  const ageText = (() => {
    if (ageMin !== undefined && ageMax !== undefined) {
      if (ageMin >= 18) return t('common:card.adults');
      if (ageMax >= 99) return t('common:card.agePlus', { min: ageMin });
      return t('common:card.ageRange', { min: ageMin, max: ageMax });
    }
    return ageRange;
  })();

  const { amount, unit } = (() => {
    if (itemType === 'pass') {
      return { amount: `$${price}`, unit: `/ ${totalSessions} ${t('common:pricing.sessions')}` };
    }
    if (itemType === 'course') {
      // Lessons can be bought individually, so the listed price is a "from".
      return { amount: t('common:pricing.startingFrom', { price }), unit: '' };
    }
    const isAdultOnly = childRequired === false;
    const shown = isAdultOnly ? (adultPrice ?? 0) : price;
    return {
      amount: priceVaries ? t('common:pricing.startingFrom', { price: shown }) : `$${shown}`,
      unit: isAdultOnly ? t('common:pricing.perAdult') : t('common:pricing.perChild'),
    };
  })();

  const PartIcon = participation ? PARTICIPATION_ICON[participation] : null;
  const badge =
    itemType === 'course'
      ? t('common:card.courseBadge')
      : itemType === 'pass'
        ? t('common:card.passBadge')
        : spotsLeft && spotsLeft <= 5
          ? t('common:card.spotsLeft', { count: spotsLeft })
          : null;
  const when =
    itemType === 'course'
      ? t('common:card.anytime')
      : [date, startTime ?? time].filter(Boolean).join(' ');

  return (
    <article className="group relative flex h-full w-full flex-col overflow-hidden rounded-[18px] border border-line bg-white text-left transition-[transform,box-shadow] duration-200 hover:-translate-y-[3px] hover:shadow-[0_22px_40px_-28px_rgba(60,50,30,0.6)] motion-reduce:transition-none motion-reduce:hover:translate-y-0">
      <div className="relative aspect-[4/3] min-h-0 overflow-hidden bg-primary-pale">
        <img
          src={imageUrl}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
        />
        {(participation || language) && (
          <div className="absolute left-2.5 top-2.5 flex items-center gap-1.5">
            {participation && PartIcon && (
              <span
                className={`inline-flex items-center gap-[5px] rounded-full bg-white py-1 pl-2 pr-2.5 font-ui text-[12.5px] font-semibold shadow-[0_4px_10px_-6px_rgba(0,0,0,0.5)] ${PARTICIPATION_TONE[participation]}`}
              >
                <PartIcon className="h-3.5 w-3.5" aria-hidden="true" />
                {t(`common:participation.${participation}`)}
              </span>
            )}
            {language && (
              <span className="rounded-full bg-[rgba(44,44,44,0.74)] px-[9px] py-[3px] font-ui text-[11.5px] font-semibold text-white">
                {t(`common:teachingLanguage.${language}`)}
              </span>
            )}
          </div>
        )}
        {badge && (
          <span className="absolute bottom-2.5 left-2.5 rounded-full bg-orange px-2.5 py-[3px] font-ui text-[12px] font-semibold text-ink">
            {badge}
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-[7px] px-[15px] pb-3 pt-[13px]">
        <h3 className="line-clamp-2 font-ui text-base font-semibold leading-[1.35] text-ink">
          {/* Stretched link: the whole card opens the detail page */}
          <Link
            to={href}
            className="after:absolute after:inset-0 after:content-[''] focus:outline-none focus-visible:after:rounded-[18px] focus-visible:after:ring-[3px] focus-visible:after:ring-primary"
          >
            {title}
          </Link>
        </h3>
        <div className="flex flex-col gap-[3px] font-text text-[13.5px] text-ink-2">
          {when && (
            <div className="flex min-w-0 items-center gap-[7px]">
              {itemType === 'course' ? (
                <Clock className="h-[15px] w-[15px] flex-none text-mute" aria-hidden="true" />
              ) : (
                <Calendar className="h-[15px] w-[15px] flex-none text-mute" aria-hidden="true" />
              )}
              <span className="min-w-0 truncate">{when}</span>
            </div>
          )}
          {(ageText || location) && (
            <div className="flex min-w-0 items-center gap-[7px]">
              <Smile className="h-[15px] w-[15px] flex-none text-mute" aria-hidden="true" />
              <span className="min-w-0 truncate">
                {[ageText, location].filter(Boolean).join(' · ')}
              </span>
            </div>
          )}
        </div>
        <div className="mt-auto flex items-center gap-0.5 pt-1">
          <div data-testid="card-price" className="mr-auto font-ui text-[17px] font-bold text-ink">
            {amount}
            {unit && <small className="text-[13px] font-medium text-mute"> {unit}</small>}
          </div>
          <button
            type="button"
            className={`${iconBtn} relative z-10`}
            aria-label={t('common:share.button')}
            onClick={() => share(href, title)}
          >
            <Share2 className="h-[18px] w-[18px]" aria-hidden="true" />
          </button>
          {canSave && (
            <button
              type="button"
              className={`${iconBtn} relative z-10 ${saved ? 'text-heart' : ''}`}
              aria-label={saved ? t('common:favorite.remove') : t('common:favorite.add')}
              aria-pressed={saved}
              onClick={() => toggleFavorite('activity', activityId!)}
            >
              <Heart
                className={`h-[18px] w-[18px] ${saved ? 'fill-current' : ''}`}
                aria-hidden="true"
              />
            </button>
          )}
        </div>
      </div>
    </article>
  );
}
